// src/components/tickets/detail/TranscriptViewer.tsx
//
// The whole call transcript (Story 4.2 AC 2), rendered as speaker-labeled turns
// in BODY typography (relaxed line-height, never monospace), collapsed to
// ~TRANSCRIPT_PREVIEW_TURNS turns with "Show full call" / "Collapse". The same
// full transcript is reachable from every ticket the call spawned; sibling
// tickets are listed under the viewer ("This call asked N questions") and
// navigable. Extraction-failed tickets render the raw transcript exactly this
// way — their authored question text already says what happened (no banner).

import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  parseTranscript,
  TRANSCRIPT_PREVIEW_TURNS,
} from '../../../utils/transcript'
import { ticketStatusBadge } from '../../../utils/ticketDisplay'
import { Badge } from '../../ui/Badge'
import type { YakSiblingTicket } from '../../../types/yak'

interface TranscriptViewerProps {
  transcript: string | null
  siblings: YakSiblingTicket[]
  currentTicketId: string
}

export function TranscriptViewer({
  transcript,
  siblings,
  currentTicketId,
}: TranscriptViewerProps) {
  const [expanded, setExpanded] = useState(false)
  const turns = parseTranscript(transcript)

  const isLong = turns.length > TRANSCRIPT_PREVIEW_TURNS
  const shown =
    expanded || !isLong ? turns : turns.slice(0, TRANSCRIPT_PREVIEW_TURNS)

  // Siblings include self; the "asked N questions" count is the whole call.
  const otherSiblings = siblings.filter(s => s.id !== currentTicketId)

  return (
    <section className="card p-6" aria-labelledby="transcript-heading">
      <h2
        id="transcript-heading"
        className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500"
      >
        Call transcript
      </h2>

      {turns.length === 0 ? (
        <p className="text-sm text-gray-500">
          No transcript was recorded for this call.
        </p>
      ) : (
        <>
          <div className="space-y-3 leading-relaxed">
            {shown.map((turn, i) => (
              <p key={i} className="text-sm text-gray-900">
                {turn.speaker ? (
                  <span className="mr-2 font-medium text-gray-500">
                    {turn.speaker}:
                  </span>
                ) : null}
                {turn.text}
              </p>
            ))}
          </div>

          {isLong ? (
            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
            >
              {expanded ? 'Collapse' : 'Show full call'}
            </button>
          ) : null}
        </>
      )}

      {siblings.length > 1 ? (
        <div className="mt-6 border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-600">
            This call asked {siblings.length} questions.
          </p>
          <ul className="mt-2 space-y-1">
            {otherSiblings.map(s => (
              <li key={s.id} className="flex items-center gap-2 text-sm">
                <Badge variant={ticketStatusBadge(s.status).variant}>
                  {ticketStatusBadge(s.status).label}
                </Badge>
                <Link
                  to={`/tickets/${s.id}`}
                  className="min-w-0 truncate text-brand-600 hover:text-brand-700 hover:underline"
                >
                  {s.question}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}
